import React from 'react';
import styled from 'styled-components';
import { ADHERENT_OPTIONS } from '../sdeConstants';

const StepContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const StepTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1F2937;
  margin-bottom: 0.5rem;
`;

const StepSubtitle = styled.p`
  color: #6B7280;
  margin-bottom: 1.25rem;
  font-size: 0.95rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.4rem;
  ${props => props.required && `&::after { content: ' *'; color: #EF4444; }`}
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  transition: border-color 0.2s;
  background: #F9FAFB;
  &:focus { outline: none; border-color: #2563EB; background: white; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const SectionDivider = styled.div`
  margin: 1.25rem 0 1rem;
  padding-top: 1rem;
  border-top: 1px solid #E5E7EB;
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 0.25rem;
  }
  p { color: #9CA3AF; font-size: 0.85rem; margin: 0; }
`;

const AdherentSection = styled.div`
  background: #F0F4FF;
  border: 1px solid #C7D2FE;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 1rem;
`;

const AdherentTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  &::after { content: ' *'; color: #EF4444; }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #374151;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.15s;
  margin: 0;
  line-height: 1.2;
  &:hover { background: #E0E7FF; }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #2563EB;
    cursor: pointer;
    margin: 0;
  }
`;

const PopAStep1_Representative = ({ data, updateData, lang = 'fr', populationType }) => {
  const isPopB = populationType === 'pop_b';
  const rep1 = data.representative_1 || {};
  const rep2 = data.representative_2 || {};

  const updateRep = (repKey, field, value) => {
    updateData({
      [repKey]: { ...data[repKey], [field]: value }
    });
  };

  const adherentList = data.metadata?.adherent_cotisant || [];
  const handleAdherentToggle = (value) => {
    let next;
    if (value === 'aucun') {
      next = adherentList.includes('aucun') ? [] : ['aucun'];
    } else {
      const without = adherentList.filter(v => v !== 'aucun');
      next = without.includes(value) ? without.filter(v => v !== value) : [...without, value];
    }
    updateData({ metadata: { ...data.metadata, adherent_cotisant: next } });
  };

  return (
    <StepContainer>
      <StepTitle>{isPopB ? (lang === 'fr' ? 'Studio de design' : 'Design Studio') : (lang === 'fr' ? 'Entreprise' : 'Company')}</StepTitle>
      <StepSubtitle>
        {lang === 'fr'
          ? 'Informations de contact pour le Speed Dating'
          : 'Contact information for the Speed Dating event'}
      </StepSubtitle>

      <FormGroup>
        <Label required>{isPopB ? (lang === 'fr' ? 'Nom du studio de design / designer' : 'Design studio / designer name') : (lang === 'fr' ? 'Nom de l\'entreprise' : 'Company Name')}</Label>
        <Input
          value={data.company_name || ''}
          onChange={e => updateData({ company_name: e.target.value })}
          placeholder={isPopB ? (lang === 'fr' ? 'Ex: Studio Dupont Design' : 'E.g., Studio Dupont Design') : (lang === 'fr' ? 'Ex: Maison & Objet' : 'E.g., Maison & Objet')}
        />
      </FormGroup>

      <FormGroup>
        <Label>{lang === 'fr' ? 'Site Internet' : 'Website'}</Label>
        <Input
          value={data.metadata?.website || ''}
          onChange={e => updateData({ metadata: { ...data.metadata, website: e.target.value } })}
          placeholder="https://www.example.com"
        />
      </FormGroup>

      <FormGroup>
        <Label>Instagram</Label>
        <Input
          value={data.metadata?.instagram || ''}
          onChange={e => updateData({ metadata: { ...data.metadata, instagram: e.target.value } })}
          placeholder="@votre_compte"
        />
      </FormGroup>

      {!isPopB && (
        <AdherentSection>
          <AdherentTitle>{lang === 'fr' ? 'Adhérent / Cotisant' : 'Member / Contributor'}</AdherentTitle>
          <CheckboxGroup>
            {ADHERENT_OPTIONS.map(opt => (
              <CheckboxLabel key={opt.value}>
                <input
                  type="checkbox"
                  checked={adherentList.includes(opt.value)}
                  onChange={() => handleAdherentToggle(opt.value)}
                />
                {opt.label}
              </CheckboxLabel>
            ))}
          </CheckboxGroup>
        </AdherentSection>
      )}

      <SectionDivider>
        <h3>{lang === 'fr' ? 'Contact principal' : 'Primary Contact'}</h3>
      </SectionDivider>

      <Row>
        <FormGroup>
          <Label required>{lang === 'fr' ? 'Nom' : 'Last Name'}</Label>
          <Input value={rep1.name || ''} onChange={e => updateRep('representative_1', 'name', e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label required>{lang === 'fr' ? 'Prénom' : 'First Name'}</Label>
          <Input value={rep1.firstname || ''} onChange={e => updateRep('representative_1', 'firstname', e.target.value)} />
        </FormGroup>
      </Row>

      <Row>
        <FormGroup>
          <Label required>Email</Label>
          <Input type="email" value={rep1.email || ''} onChange={e => updateRep('representative_1', 'email', e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label required>{lang === 'fr' ? 'Téléphone mobile' : 'Mobile Phone'}</Label>
          <Input type="tel" value={rep1.phone || ''} onChange={e => updateRep('representative_1', 'phone', e.target.value)} placeholder="+33 6 12 34 56 78" />
        </FormGroup>
      </Row>

      <Row>
        <FormGroup>
          <Label>{lang === 'fr' ? 'Téléphone fixe' : 'Landline'}</Label>
          <Input type="tel" value={rep1.landline || ''} onChange={e => updateRep('representative_1', 'landline', e.target.value)} placeholder="+33 1 23 45 67 89" />
        </FormGroup>
        <FormGroup>
          <Label required>{lang === 'fr' ? 'Intitulé du poste' : 'Job Title'}</Label>
          <Input value={rep1.job_title || ''} onChange={e => updateRep('representative_1', 'job_title', e.target.value)} placeholder={lang === 'fr' ? 'Ex: Directeur commercial' : 'E.g., Sales Director'} />
        </FormGroup>
      </Row>

      <SectionDivider>
        <h3>{lang === 'fr' ? 'Second contact (optionnel)' : 'Secondary Contact (optional)'}</h3>
        <p>{lang === 'fr' ? 'Ajoutez un second participant si nécessaire' : 'Add a second participant if needed'}</p>
      </SectionDivider>

      <Row>
        <FormGroup>
          <Label>{lang === 'fr' ? 'Nom' : 'Last Name'}</Label>
          <Input value={rep2.name || ''} onChange={e => updateRep('representative_2', 'name', e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>{lang === 'fr' ? 'Prénom' : 'First Name'}</Label>
          <Input value={rep2.firstname || ''} onChange={e => updateRep('representative_2', 'firstname', e.target.value)} />
        </FormGroup>
      </Row>

      <Row>
        <FormGroup>
          <Label>Email</Label>
          <Input type="email" value={rep2.email || ''} onChange={e => updateRep('representative_2', 'email', e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>{lang === 'fr' ? 'Téléphone mobile' : 'Mobile Phone'}</Label>
          <Input type="tel" value={rep2.phone || ''} onChange={e => updateRep('representative_2', 'phone', e.target.value)} />
        </FormGroup>
      </Row>

      <Row>
        <FormGroup>
          <Label>{lang === 'fr' ? 'Téléphone fixe' : 'Landline'}</Label>
          <Input type="tel" value={rep2.landline || ''} onChange={e => updateRep('representative_2', 'landline', e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>{lang === 'fr' ? 'Intitulé du poste' : 'Job Title'}</Label>
          <Input value={rep2.job_title || ''} onChange={e => updateRep('representative_2', 'job_title', e.target.value)} />
        </FormGroup>
      </Row>
    </StepContainer>
  );
};

export default PopAStep1_Representative;
